import '@orobix/label-studio/build/static/css/main.css'

import LabelStudio, { LSTask } from '@orobix/label-studio'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Annotation, AnnotationType } from '../../types/annotation'
import { Observation } from '../../types/observation'
import { get } from '../../utils/api'
import { handleApiErrors } from '../../utils/apiErrors'
import { getQueryString } from '../../utils/queryString'
import { ObservationsParams, Results } from '../Observations/types'
import {
  ObservationsParamsAll,
  useObservationsAll,
} from '../ObservationsBrowser/useObservationsAll'
import { getLightweightParams } from '../ObservationsBrowser/utils'
import { titleUneditableObservation } from '../ObservationsSelectField/utils'
import AnnotatorContainer from './AnnotatorContainer'
import { makeConfigFor } from './config'
import { Topbar } from './Helpers/Topbar'
import { LabelStudioAnnotator } from './LabelStudioAnotator'
import { makeTask } from './task'
import { AnnotationLabel, AnnotationParams } from './types'
import { useAnnotationLabels } from './useAnnotationLabels'
import { useCreateAnnotation } from './useCreateAnnotation'
import { useInterfaces } from './useInterfaces'
import { useUser } from './useUser'
import { prepareLabels } from './utils'

interface AnnotatorProps {
  params: AnnotationParams
  productId: number
  annotationType: AnnotationType
  mode?: 'editing' | 'viewing'
  withLightbox?: boolean
  noSkip?: boolean
  onAnnotationCreated?: (o: Observation) => void
  onCloseRequest?: () => void
  defaultFilters?: ObservationsParams
  dataset?: string[]
  selectedObservationId?: number
}

export const Annotator = ({
  params,
  productId,
  annotationType,
  onAnnotationCreated,
  mode = 'editing',
  withLightbox = false,
  noSkip = false,
  onCloseRequest,
  defaultFilters,
  dataset,
  selectedObservationId,
}: AnnotatorProps) => {
  const { t } = useTranslation()
  const labelStudioRef = useRef<LabelStudio | null>(null)
  const defaultLightweightParams = getLightweightParams('grid', false)

  const { interfaces, hasInterface } = useInterfaces({
    noSkip,
    noEdit: mode === 'viewing',
  })

  const user = useUser()
  const { data: labels } = useAnnotationLabels(productId)
  const { mutate: createAnnotation } = useCreateAnnotation()
  const [task, setTask] = useState<LSTask>()

  const makeConfig = makeConfigFor(annotationType)
  const [config, setConfig] = useState(makeConfig(prepareLabels(labels || [])))

  const [paramsAll] = useState<ObservationsParamsAll>({
    productId,
    trainingId: params.trainingId,
    ...defaultFilters,
    ...defaultLightweightParams,
    dataset,
  })

  const { dataAll: observations, fetchAll } = useObservationsAll(paramsAll)
  const [currentObservationId, setCurrentObservationId] = useState(
    selectedObservationId
  )

  function isEditable(obsId?: number) {
    let currentObservation = observations?.find(
      (o) => o.id === currentObservationId
    )
    if (obsId && obsId > 0) {
      currentObservation = observations?.find((o) => o.id === obsId)
    }

    const hazInterface: boolean = hasInterface(['submit', 'update'])
    const notUsedInTraining =
      (currentObservation?.trainingIds ?? []).length === 0

    return hazInterface && notUsedInTraining
  }

  const parentContainer = document.getElementById('asideDivId')?.parentElement
  if (parentContainer) {
    // convert document.documentElement.scrollHeight pixel to vh
    const newHeight =
      (document.documentElement.scrollHeight / window.innerHeight) * 100
    parentContainer.style.height = `${newHeight}vh`
  }

  function moveLabelToTop() {
    const childDiv = []
    if (annotationType === 'Tag') {
      childDiv.push(
        document.getElementsByClassName(
          'lsf-choices lsf-choices_layout_vertical'
        )[0]
      )
    } else if (annotationType === 'Brush' || annotationType === 'Polyline') {
      childDiv.push(
        document.getElementsByClassName('lsf-labels lsf-labels_inline')[0]
      )
    } else {
      // eslint-disable-next-line no-console
      console.error('Annotation type ', annotationType, ' not handled yet')
    }

    if (childDiv.length > 0) {
      const parentDiv = childDiv[0].parentElement
      if (parentDiv) {
        parentDiv.setAttribute(
          'style',
          'display: flex; flex-direction: column-reverse'
        )
      }
    }
  }

  function showHideAnnotatorBrush() {
    if (!isEditable()) {
      const editButtons = []
      if (annotationType === 'Tag') {
        // Tag -> Anomaly or Classification
        editButtons.push(
          document.getElementsByClassName(
            'lsf-choices lsf-choices_layout_vertical'
          )[0]
        )
      } else if (annotationType === 'Brush' || annotationType === 'Polyline') {
        editButtons.push(
          document.getElementsByClassName('lsf-labels lsf-labels_inline')[0]
        )
        editButtons.push(
          document.getElementsByClassName('lsf-toolbar__group')[1]
        )
        editButtons.push(
          document.getElementsByClassName('lsf-entities__source')[0]
        )
      } else {
        // eslint-disable-next-line no-console
        console.error('Annotation type ', annotationType, ' not handled yet')
      }

      for (let i = 0; i < editButtons.length; i += 1) {
        if (editButtons[i]) {
          editButtons[i].setAttribute('style', 'display: none')
        }
      }
    }
  }

  const renderTopbarTitle = (): string => {
    if (!observations || !currentObservationId) return ''

    const idx = observations.findIndex((o) => o.id === currentObservationId)
    if (idx === -1) return ''

    return `#${observations[idx].id} (${idx + 1} / ${observations.length})`
  }

  const [topbarTitle, setTopbarTitle] = useState(renderTopbarTitle())

  const renderTopbarFilename = (): string => {
    if (!observations || !currentObservationId) return ''

    const idx = observations.findIndex((o) => o.id === currentObservationId)
    if (idx === -1) return ''

    return observations[idx].filename
  }
  const [topbarFilename, setTopbarFilename] = useState(renderTopbarFilename())

  const onLabelUpdated = (updatedLabels: Array<AnnotationLabel>) => {
    setConfig(makeConfig(prepareLabels(updatedLabels)))
    setTimeout(() => {
      moveLabelToTop()
    }, 100)
  }

  type AnnotationResult = Results<Annotation>

  useLayoutEffect(() => {
    setTimeout(() => {
      moveLabelToTop()
      showHideAnnotatorBrush()
    }, 100)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task, observations])

  // Apply the config when the labels are loaded
  useEffect(() => {
    if (labels) {
      onLabelUpdated(labels)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labels])

  // Fetch all observations on mount
  useEffect(() => {
    fetchAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setCurrentObservationId(selectedObservationId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedObservationId])

  // Update task when the current observation changes
  useEffect(() => {
    if (!currentObservationId) return
    setTask(undefined)

    // Fetch current observation data
    get<Observation>(`/observations/${currentObservationId}/`)
      .then((response) => {
        const queryString = getQueryString({
          observation: currentObservationId,
          training: params.trainingId,
          trainingTest: params.trainingTestId,
          annotationSource: params.annotationSource,
          limit: 1,
        })

        // Fetch current annotation data
        get<AnnotationResult>(`/observation_annotations/?${queryString}`)
          .then((res) => {
            setTask(makeTask(response, res.results[0], user.email))
          })
          .catch(handleApiErrors)
      })
      .catch(handleApiErrors)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentObservationId, params.annotationSource])

  useEffect(() => {
    setTopbarTitle(renderTopbarTitle())
    setTopbarFilename(renderTopbarFilename())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentObservationId, observations])

  const isLoading = !labels || !task || !config || !user || !interfaces

  const [isSaving, setIsSaving] = useState(false)

  const updateAndNext = (o: Observation) => {
    if (onAnnotationCreated) onAnnotationCreated(o)
  }

  // On 'Save' click, create the annotation
  const handleCreateAnnotation = () => {
    setIsSaving(true)

    const instance = labelStudioRef.current
    const selectedAnnotation = instance?.annotationStore?.selected

    if (instance && selectedAnnotation && currentObservationId) {
      // label studio preflight checks
      // AIG-2171: commented this code to solve this issue
      // selectedAnnotation.beforeSend()
      if (!selectedAnnotation.validate()) {
        setIsSaving(false)
        return undefined
      }

      const data = JSON.parse(
        JSON.stringify(selectedAnnotation.serializeAnnotation())
      )

      // Check for any annotation with different size width/height
      const differentWidth: number[] = []
      const differentHeight: number[] = []

      for (let i = 0; i < data.length; i += 1) {
        differentWidth.push(data[i].original_width)
        differentHeight.push(data[i].original_height)
      }
      const uniqueDifferentWidth = [...new Set(differentWidth)]
      const uniqueDifferentHeight = [...new Set(differentHeight)]
      if (uniqueDifferentHeight.length > 1 || uniqueDifferentWidth.length > 1) {
        toast.error(
          t(
            'Annotation not saved. Please refresh the page and try annotating again. If the problem persists, contact the administrator.'
          )
        )
        setIsSaving(false)
        // eslint-disable-next-line no-console
        console.error(data)
        return undefined
      }

      selectedAnnotation.sendUserGenerate()
      selectedAnnotation.dropDraft()

      // then, create annotation
      return createAnnotation(
        {
          observationId: currentObservationId,
          annotationType,
          data,
        },
        {
          onSuccess: (o) => {
            setIsSaving(false)
            updateAndNext(o)
          },
          onError: () => {
            setIsSaving(false)
          },
        }
      )
    }

    setIsSaving(false)
    return undefined
  }

  // On 'Skip' or R-arrow click, go to the next observation
  const handleNextRequest = () => {
    if (isLoading || !observations) return

    const currentObservation = observations.find(
      (o) => o.id === currentObservationId
    )
    const idx = currentObservation
      ? observations.indexOf(currentObservation)
      : -1

    let nextObservationIndex = 0
    let nextObservation = observations?.[nextObservationIndex]
    if (idx + 1 < observations.length) {
      nextObservationIndex = idx + 1
      nextObservation = observations?.[nextObservationIndex]
    }
    // Automatic skip of non-editable observations
    // eslint-disable-next-line no-constant-condition
    const imagesIdChecked: number[] = []
    while (true) {
      const obsId = nextObservation?.id
      if (
        obsId === undefined ||
        isEditable(obsId) ||
        imagesIdChecked.includes(obsId)
      ) {
        break
      }
      imagesIdChecked.push(obsId)
      nextObservationIndex += 1
      nextObservation = observations?.[nextObservationIndex]
      if (nextObservationIndex + 1 >= observations.length) {
        nextObservationIndex = -1
      }
    }

    setCurrentObservationId(nextObservation?.id)
  }

  // On L-arrow click, go to the previous observation
  const handlePrevRequest = () => {
    if (isLoading || !observations) return

    const currentObservation = observations.find(
      (o) => o.id === currentObservationId
    )
    const idx = currentObservation
      ? observations.indexOf(currentObservation)
      : -1

    let prevObservationIndex = idx - 1
    let prevObservation = observations?.[prevObservationIndex]
    if (prevObservationIndex < 0) {
      const observationsLength = observations?.length
      prevObservationIndex = observationsLength - 1
      prevObservation = observations?.[prevObservationIndex]
    }

    // Automatic skip of non-editable observations
    // eslint-disable-next-line no-constant-condition
    const imagesIdChecked: number[] = []
    while (true) {
      const obsId = prevObservation?.id
      if (
        obsId === undefined ||
        isEditable(obsId) ||
        imagesIdChecked.includes(obsId)
      ) {
        break
      }
      imagesIdChecked.push(obsId)
      prevObservationIndex -= 1
      prevObservation = observations?.[prevObservationIndex]
      if (prevObservationIndex + 1 >= observations.length) {
        prevObservationIndex = -1
      }
    }

    setCurrentObservationId(prevObservation?.id)
  }

  const currentObservation = observations?.find(
    (o) => o.id === currentObservationId
  )
  const readonlyMessage =
    (currentObservation?.trainingIds ?? []).length > 0
      ? titleUneditableObservation(currentObservation?.trainingIds ?? [])
      : undefined

  return (
    <AnnotatorContainer
      withLightbox={withLightbox}
      isLoading={isLoading}
      readonlyMessage={readonlyMessage}
      topbar={
        <Topbar
          title={topbarTitle}
          filename={topbarFilename}
          productId={productId}
          isLoading={isLoading}
          isSaving={isSaving}
          onClose={onCloseRequest}
          onPrev={handlePrevRequest}
          onNext={handleNextRequest}
          onLabelUpdated={onLabelUpdated}
          onSkip={hasInterface(['skip']) ? handleNextRequest : undefined}
          onSave={isEditable() ? handleCreateAnnotation : undefined}
          annotationType={annotationType}
        />
      }
    >
      <LabelStudioAnnotator
        labelStudioRef={labelStudioRef}
        task={task}
        config={config}
        interfaces={interfaces}
        user={user}
      />
    </AnnotatorContainer>
  )
}

export default Annotator
